import type { ContentBlob, Codec } from "@vmeste/shared";

/**
 * Reading encrypted content without making every screen asynchronous.
 *
 * ## The problem this exists for
 *
 * In `none` mode a blob carries plain JSON, so decoding is a property access
 * and every screen decodes at render, synchronously. AES-GCM is asynchronous.
 * Turning on end-to-end encryption therefore threatens to turn every list row,
 * every message and every wish title into a promise — which is a rewrite of the
 * whole client, and the reason `e2ee` sat unimplemented behind a codec that was
 * ready for it.
 *
 * ## What happens instead
 *
 * Decryption moves to where a delta is applied, which is already asynchronous,
 * and the result is kept here. Screens ask this module and get an answer now.
 * The nine places that used to read `blob.data` directly each change by one
 * line, and nothing above them learns what mode a space is in — which was
 * always the promise of the codec boundary (SPEC §3).
 *
 * ## Why the ciphertext is the key
 *
 * A row's id would be wrong: editing a message gives it new content under the
 * same id, and the old entry would shadow the new one. A revision number would
 * work for synced rows and not for the optimistic ones, which have none yet.
 * The ciphertext, on the other hand, *is* the content — same bytes, same
 * plaintext, always — and it is already unique per write because every encode
 * draws a fresh iv.
 */

/**
 * How many decoded values to keep.
 *
 * Entries are small — a title, a line of a list, a message — and a couple
 * writes a few thousand of them a year. This is a ceiling against a runaway,
 * not a working limit, and eviction is oldest-first because the newest content
 * is what is on screen.
 */
const MAX_ENTRIES = 20_000;

const cache = new Map<string, unknown>();

/** Same bytes, same plaintext. Every encode draws a fresh iv, so this is unique. */
function keyOf(blob: ContentBlob): string | null {
  return blob.mode === "e2ee" ? `${blob.iv}.${blob.ct}` : null;
}

/**
 * The decoded content, or `undefined` when it is not readable here.
 *
 * `undefined` means one of two things and callers treat them the same: this
 * device has no key for the space, or the blob has not been decrypted yet. Both
 * are "show the placeholder", which is what every call site already did for a
 * blob it could not read.
 */
export function plaintextOf(blob: ContentBlob | null | undefined): unknown {
  if (!blob) return undefined;
  if (blob.mode === "none") return blob.data;

  const key = keyOf(blob);
  return key === null ? undefined : cache.get(key);
}

/** Remember one decoded value. Called by the codec wrapper and by priming. */
export function rememberPlaintext(blob: ContentBlob, value: unknown): void {
  const key = keyOf(blob);
  if (key === null) return;

  // Re-inserting moves it to the end, which is what makes the eviction below
  // oldest-first rather than arbitrary.
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    cache.delete(oldest.value);
  }
}

/**
 * Decrypt everything in a batch of blobs that has not been decrypted yet.
 *
 * Called with a delta's worth of content before the delta is applied, so that
 * by the time anything renders, every value it needs is already here.
 *
 * A blob that fails to decrypt is skipped rather than thrown on. One row
 * written with a key this device does not have — a space whose passphrase was
 * changed, a half-finished migration — must not stop the other four hundred
 * from appearing.
 */
export async function primePlaintext(
  blobs: readonly (ContentBlob | null | undefined)[],
  codec: Codec,
): Promise<void> {
  if (codec.mode === "none") return;

  const pending: ContentBlob[] = [];
  const seen = new Set<string>();

  for (const blob of blobs) {
    if (!blob || blob.mode !== "e2ee") continue;
    const key = keyOf(blob)!;
    if (cache.has(key) || seen.has(key)) continue;
    seen.add(key);
    pending.push(blob);
  }

  await Promise.all(
    pending.map(async (blob) => {
      try {
        // Decoded without a schema on purpose: this layer does not know what
        // shape any given row should be, and each screen validates its own when
        // it reads. A blob that decrypts to nonsense fails there, loudly, with
        // the schema that expected something else.
        const value = await codec.decode(blob, PASSTHROUGH);
        rememberPlaintext(blob, value);
      } catch {
        // Wrong key, or tampering. Skipped; the screen shows its placeholder.
      }
    }),
  );
}

/**
 * A schema that accepts anything.
 *
 * The codec insists on validation because a blob decrypting to an unexpected
 * shape must never reach the UI half-typed. That rule is kept — it is enforced
 * at each screen, against the schema that screen actually expects — and this
 * stands in for it here, where the shape is genuinely unknown.
 */
const PASSTHROUGH = {
  safeParse: (value: unknown) => ({ success: true as const, data: value }),
} as never;

/** Forget everything. Signing out must not leave decrypted content in memory. */
export function forgetPlaintext(): void {
  cache.clear();
}

/** For tests and for the settings screen's "what is cached" line. */
export function plaintextCount(): number {
  return cache.size;
}
